/**
 * Enum for survey question types.
 * These values correspond to the question types supported by PostHog surveys.
 */
export const SurveyQuestionType = {
  SINGLE_CHOICE: "single_choice",
  RATING: "rating",
  OPEN: "open",
} as const;

/**
 * Enum for reasons for visiting the Commons Deep Dive.
 * These values are used to categorize user intentions for a deep dive survey.
 */
export const ReasonForVisitingDeepDive = {
  INNOVATORS: "innovators",
  INGREDIENTS: "ingredients",
  FORMULATIONS: "formulations",
  PERFORMANCE: "performance",
  SUPPORT: "support",
  OTHER: "other",
} as const;

/**
 * Options for the 'reason for visiting deep dive' dropdown.
 * Each option includes a value and a descriptive label.
 */
export const REASONS_FOR_VISITING_DEEPDIVE = [
  {
    value: ReasonForVisitingDeepDive.INNOVATORS,
    label: "Find material innovators and product solutions",
  },
  {
    value: ReasonForVisitingDeepDive.INGREDIENTS,
    label: "Learn about natural ingredients",
  },
  {
    value: ReasonForVisitingDeepDive.FORMULATIONS,
    label: "Find bio-based material formulations",
  },
  {
    value: ReasonForVisitingDeepDive.PERFORMANCE,
    label: "Understand the performance of bio-based materials",
  },
  {
    value: ReasonForVisitingDeepDive.SUPPORT,
    label: "Support for improving my material formulation",
  },
  { value: ReasonForVisitingDeepDive.OTHER, label: "Something else" },
];

/**
 * Enum for user self-identified as.
 * These values are used to categorize user self-identification.
 */
export const UserSelfIdentifiedAs = {
  STUDENT: "student",
  ACADEMIC_RESEARCHER: "academic_researcher",
  EDUCATOR: "educator",
  COMMERCIAL_MATERIALS_DEVELOPER: "commercial_materials_developer",
  CONSUMER_BRAND: "consumer_brand",
  CONSULTANT: "consultant",
  OTHER: "other",
} as const;

/**
 * Options for the 'user self-identified as' dropdown.
 * Each option includes a value and a descriptive label.
 */
export const USER_SELF_IDENTIFIED_AS_OPTIONS = [
  { value: UserSelfIdentifiedAs.STUDENT, label: "Student" },
  {
    value: UserSelfIdentifiedAs.ACADEMIC_RESEARCHER,
    label: "Academic Researcher",
  },
  { value: UserSelfIdentifiedAs.EDUCATOR, label: "Educator" },
  {
    value: UserSelfIdentifiedAs.COMMERCIAL_MATERIALS_DEVELOPER,
    label: "Commercial Materials Developer",
  },
  { value: UserSelfIdentifiedAs.CONSUMER_BRAND, label: "Consumer Brand" },
  { value: UserSelfIdentifiedAs.CONSULTANT, label: "Consultant" },
  { value: UserSelfIdentifiedAs.OTHER, label: "Other" },
];

/**
 * URL for the Calendly booking page.
 */
export const CALENDLY_BOOKING_URL =
  "https://calendly.com/liz-cpa/materiom-ai-v2-trial-feedback-clone";

/**
 * Constants for the Materiom AI v2 feedback survey.
 */
export const MATERIOM_AI_V2_FEEDBACK_SURVEY = {
  FORM_TITLE: "Please take a moment to share your feedback",
  LAST_PAGE_TITLE: "Thank you!",
  LAST_PAGE_MESSAGE: `Your feedback is invaluable.

Want to chat more? We'd love to hear additional thoughts — book some time with us [here](${CALENDLY_BOOKING_URL}).`,
} as const;

/**
 * Constants for the Materiom AI v2 onboarding survey.
 * This survey is shown once to new users to learn more about them.
 */
export const MATERIOM_AI_V2_ONBOARDING_SURVEY = {
  FORM_TITLE: "Welcome! Tell us about yourself",
  LAST_PAGE_TITLE: "Thank you!",
  LAST_PAGE_MESSAGE:
    "Thanks for sharing! Your responses help us tailor Materiom AI to your needs.",
} as const;
